import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { useEffect } from "react";
import "./App.css";

// Layouts
import MainLayout from "./layouts/MainLayout";
import AuthLayout from "./layouts/AuthLayout";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import HomePage from "./pages/HomePage";
import FileUploadPage from "./pages/FileUploadPage";
import ResultsPage from "./pages/ResultsPage";
import UserImagesPage from "./pages/UserImagesPage";
import PurchasePage from "./pages/PurchasePage";

// Auth Store
import { useAuthStore } from "./store/authStore";
import PurchaseSuccessPage from "./pages/PurchaseSuccessPage";
import i18n from "./i18n";

function App() {
  // Get state and actions from the store
  // We get initializeAuthListener directly via getState in useEffect to avoid
  // subscribing App to all authStore changes unnecessarily.
  // We subscribe to isAuthenticated for route protection.
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  // Initialize the auth listener on app load
  useEffect(() => {
    // Get the action directly from the store's state
    const { initializeAuthListener } = useAuthStore.getState();
    // Call the action to set up the listener and get the unsubscribe function
    const unsubscribe = initializeAuthListener();

    // Return the cleanup function to unsubscribe when the App unmounts
    return () => {
      unsubscribe();
    };
  }, []); // Empty dependency array ensures this runs only once on mount

  useEffect(() => {
    // Get URL search params using window.location
    const urlParams = new URLSearchParams(window.location.search);
    const lngParam = urlParams.get("lng");

    if (lngParam && lngParam !== i18n.language) {
      i18n.changeLanguage(lngParam).catch((err) => {
        console.error("Failed to change language based on URL param:", err);
      });
    }
  }, []);

  // Protected route component
  const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
    if (!isAuthenticated) {
      return <Navigate to="/login" replace />;
    }
    return <>{children}</>;
  };

  return (
    <Router>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<LandingPage />} />

        {/* Auth routes */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
        </Route>

        {/* Protected routes */}
        <Route element={<MainLayout />}>
          <Route
            path="/home"
            element={
              <ProtectedRoute>
                <HomePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/upload/:style"
            element={
              <ProtectedRoute>
                <FileUploadPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/results"
            element={
              <ProtectedRoute>
                <ResultsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-images"
            element={
              <ProtectedRoute>
                <UserImagesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/purchase"
            element={
              <ProtectedRoute>
                <PurchasePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/purchase/success"
            element={
              <ProtectedRoute>
                <PurchaseSuccessPage />
              </ProtectedRoute>
            }
          />
        </Route>

        {/* Fallback route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
